import { useState } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { useLanguage } from "../i18n";
import { SERVICE_IMAGES, LOT_PHOTOS } from "../data/cars";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";

const BeforeAfterGallery = () => {
  const { t, lang } = useLanguage();
  const [pos, setPos] = useState(50);
  const [lightbox, setLightbox] = useState(null);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative border-t border-white/10 bg-[#0D0F14] py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between"
        >
          <div>
            <p className="font-mono-accent text-xs font-medium uppercase tracking-[0.35em] text-[#E63946]">
              {t.gallery.eyebrow}
            </p>
            <h2 className="font-display mt-3 max-w-xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
              {t.gallery.title}
            </h2>
          </div>
          <div className="max-w-md">
            <p className="text-sm leading-relaxed text-[#9E9E9E]">{t.gallery.sub}</p>
            <p
              data-testid="gallery-note"
              className="font-mono-accent mt-2 text-[11px] uppercase tracking-widest text-[#FFD166]"
            >
              {t.gallery.note}
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          data-testid="before-after-frame"
          className="relative mt-12 aspect-[4/3] w-full select-none overflow-hidden border border-white/10 sm:aspect-[16/8]"
        >
          <img
            src={SERVICE_IMAGES.paint}
            alt={t.gallery.after}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <span
            data-testid="after-badge"
            className="font-mono-accent absolute right-4 top-4 rounded-full bg-[#00F5D4] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#0A0B0E]"
          >
            {t.gallery.after}
          </span>

          <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
            <img
              src={SERVICE_IMAGES.paint}
              alt={t.gallery.before}
              className="h-full w-full object-cover brightness-[0.6] contrast-125 grayscale"
            />
            <span
              data-testid="before-badge"
              className="font-mono-accent absolute left-4 top-4 rounded-full bg-[#E63946] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white"
            >
              {t.gallery.before}
            </span>
          </div>

          <div className="pointer-events-none absolute inset-y-0" style={{ left: `${pos}%` }}>
            <div className="absolute inset-y-0 -ml-px w-0.5 bg-white/90" />
            <div className="absolute top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#0A0B0E] shadow-xl">
              <MoveHorizontal className="h-5 w-5" />
            </div>
          </div>

          <input
            type="range"
            min="0"
            max="100"
            value={pos}
            onChange={(e) => setPos(Number(e.target.value))}
            data-testid="before-after-slider"
            aria-label={`${t.gallery.before} / ${t.gallery.after}`}
            className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="mt-20 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between"
        >
          <h3 className="font-display max-w-xl text-2xl font-bold tracking-tight text-white sm:text-3xl">
            {t.gallery.lotTitle}
          </h3>
          <p className="max-w-md text-sm leading-relaxed text-[#9E9E9E]">{t.gallery.lotSub}</p>
        </motion.div>

        <div
          data-testid="lot-photo-grid"
          className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-4"
        >
          {LOT_PHOTOS.map((photo, i) => (
            <motion.button
              key={photo.src}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
              onClick={() => setLightbox(i)}
              data-testid={`lot-photo-${i}`}
              className="group relative aspect-[3/4] overflow-hidden border border-white/10 transition-colors duration-300 hover:border-[#E63946]/50"
              aria-label={photo.alt[lang]}
            >
              <img
                src={photo.src}
                alt={photo.alt[lang]}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-[#0A0B0E]/15 transition-opacity duration-500 group-hover:opacity-0" />
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={lightbox !== null} onOpenChange={() => setLightbox(null)}>
        <DialogContent
          data-testid="lot-lightbox"
          className="max-w-2xl border-white/10 bg-[#0A0B0E] p-2"
        >
          <DialogTitle className="sr-only">
            {lightbox !== null ? LOT_PHOTOS[lightbox].alt[lang] : "Photo"}
          </DialogTitle>
          {lightbox !== null && (
            <img
              src={LOT_PHOTOS[lightbox].src}
              alt={LOT_PHOTOS[lightbox].alt[lang]}
              className="max-h-[80vh] w-full object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default BeforeAfterGallery;
